import { addMonths, format, startOfMonth } from 'date-fns'
import { enGB } from 'date-fns/locale'
import { FC, Suspense, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AutoFixHigh, ContentCopy, InfoOutlined, Stop } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

import GRCircularProgress from '../../../../../components/GRCircularProgress/GRCircularProgress'
import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { useListTextDefinitionsQuery } from '../../../../api/ai-prompts'
import { LazyHTMLEditor } from '../../../../components/HTMLEditor/LazyHTMLEditor'
import { useStreamSummary } from '../../hooks/useStreamSummary'

export const formatAsOfDate = (recapMonth: Date | null): string | undefined =>
  recapMonth ? format(startOfMonth(addMonths(recapMonth, 1)), 'yyyy-MM-dd') : undefined

type MonthlyRecapAIAssistantProps = {
  gameId: string
  getExistingContent: () => string | undefined
  onInsert: (value: string) => void
}

export const MonthlyRecapAIAssistant: FC<MonthlyRecapAIAssistantProps> = ({ gameId, getExistingContent, onInsert }) => {
  const { t } = useTranslation()
  const { data, status, start, cancel } = useStreamSummary()
  const [extraInstructions, setExtraInstructions] = useState('')
  const [recapMonth, setRecapMonth] = useState<Date | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null)

  const { data: textDefinitions, isError: isPromptListError } = useListTextDefinitionsQuery()

  const DEFAULT_PROMPT = 'MONTHLY_RECAP'

  useEffect(() => {
    if (selectedPrompt !== null || !textDefinitions?.length) return
    const match = textDefinitions.find((d) => d.name === DEFAULT_PROMPT)
    setSelectedPrompt(match ? match.name : textDefinitions[0].name)
  }, [textDefinitions, selectedPrompt])

  const formattedAsOfDate = formatAsOfDate(recapMonth)

  const handleGenerate = useCallback(() => {
    if (!selectedPrompt || !gameId) return
    const existingContent = getExistingContent()
    if (existingContent) {
      setConfirmDialogOpen(true)
    } else {
      start({ gameId, prompt: selectedPrompt, promptVersion: null, extraInstructions: extraInstructions || undefined, asOfDate: formattedAsOfDate })
    }
  }, [gameId, selectedPrompt, extraInstructions, formattedAsOfDate, getExistingContent, start])

  const handleConfirm = useCallback(() => {
    if (!selectedPrompt || !gameId) return
    setConfirmDialogOpen(false)
    start({ gameId, prompt: selectedPrompt, promptVersion: null, extraInstructions: extraInstructions || undefined, asOfDate: formattedAsOfDate })
  }, [gameId, selectedPrompt, extraInstructions, formattedAsOfDate, start])

  const handleInsert = useCallback(() => {
    onInsert(data)
  }, [data, onInsert])

  const isStreaming = status === 'streaming'
  const hasResult = status === 'done' || status === 'cancelled'
  const hasError = status === 'error'

  return (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Typography variant="subtitle2" fontWeight="bold">
            {t('internal-live-events:monthly_recap_section_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('internal-live-events:monthly_recap_section_instructions')}
          </Typography>
        </Grid>
        <Grid item>
          <TextField
            size="small"
            multiline
            rows={2}
            label={t('internal-live-events:monthly_recap_extra_instructions')}
            variant="outlined"
            value={extraInstructions}
            onChange={(e) => setExtraInstructions(e.target.value)}
            fullWidth
            disabled={isStreaming}
          />
        </Grid>

        <Grid item>
          <Grid container spacing={1} alignItems="center">
            {!isStreaming && (
              <>
                <Grid item>
                  <LoadingButton variant="contained" startIcon={<AutoFixHigh />} onClick={handleGenerate} size="small" disabled={!selectedPrompt || !gameId}>
                    {t('internal-live-events:generate_monthly_recap')}
                  </LoadingButton>
                </Grid>
                <Grid item>
                  {isPromptListError ? (
                    <Typography variant="body2" color="error">
                      {t('internal-live-events:monthly_recap_prompt_load_error')}
                    </Typography>
                  ) : (
                    selectedPrompt !== null && (
                      <Autocomplete
                        size="small"
                        sx={{ minWidth: 240 }}
                        options={(textDefinitions || []).map((d) => d.name)}
                        value={selectedPrompt}
                        onChange={(_e, value) => setSelectedPrompt(value || null)}
                        renderInput={(params) => <TextField {...params} label={t('internal-live-events:monthly_recap_prompt_definition')} />}
                        disableClearable
                      />
                    )
                  )}
                </Grid>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                    <DatePicker
                      label={t('internal-live-events:monthly_recap_month')}
                      value={recapMonth}
                      onChange={(date) => setRecapMonth(date)}
                      views={['year', 'month']}
                      openTo="month"
                      maxDate={new Date()}
                      format="MM.yyyy"
                      slotProps={{
                        textField: { size: 'small', sx: { width: 160 } },
                        field: { clearable: true },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item>
                  <Tooltip title={t('internal-live-events:monthly_recap_month_tooltip')}>
                    <InfoOutlined fontSize="small" color="action" sx={{ cursor: 'help', verticalAlign: 'middle' }} />
                  </Tooltip>
                </Grid>
              </>
            )}

            {isStreaming && (
              <Grid item>
                <Tooltip title={t('internal-live-events:stop_generating')}>
                  <IconButton size="small" onClick={cancel} color="warning">
                    <Stop />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}

            {hasResult && data && (
              <Grid item>
                <Tooltip title={t('internal-live-events:insert_into_editor')}>
                  <IconButton size="small" onClick={handleInsert} color="primary">
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}

            {isStreaming && (
              <Grid item>
                <CircularProgress size={20} />
              </Grid>
            )}
          </Grid>
        </Grid>

        {(isStreaming || hasResult) && (
          <Grid item>
            <Suspense fallback={<GRCircularProgress />}>
              <LazyHTMLEditor
                value={data}
                onEditorChange={() => {}}
                init={{
                  setup: (editor: any) => editor.mode.set('readonly'),
                  height: 500,
                  menubar: false,
                  toolbar: false,
                  convert_urls: false,
                }}
                disabled
              />
            </Suspense>
          </Grid>
        )}

        {hasError && (
          <Grid item>
            <Box color="error.main" typography="body2">
              {t('internal-live-events:monthly_recap_stream_error')}
            </Box>
          </Grid>
        )}
      </Grid>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <GRDialogTitle onClose={() => setConfirmDialogOpen(false)}>{t('internal-live-events:monthly_recap_replace_confirm_title')}</GRDialogTitle>
        <DialogContent>
          <DialogContentText>{t('internal-live-events:monthly_recap_replace_confirm_message')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>{t('internal-common:cancel')}</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            {t('internal-common:continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
